import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  getDocs,
  doc,
  updateDoc,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import TimeTakenField from "./TimeTakenField";
import {
  HOURS_MIN,
  buildTimeTakenCore,
  validateTimeTaken,
} from "./timeEntry";

// ---------------- utils ----------------
function cn(...c) { return c.filter(Boolean).join(" "); }
function pad2(n) { return String(n).padStart(2, "0"); }
function isoDate(d) {
  const y = d.getFullYear();
  const m = pad2(d.getMonth() + 1);
  const day = pad2(d.getDate());
  return `${y}-${m}-${day}`;
}
function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0,0,0,0);
  return x;
}
function endOfDay(d) {
  const x = new Date(d);
  x.setHours(23,59,59,999);
  return x;
}
function ymString(year, month1to12) {
  return `${year}-${pad2(month1to12)}`;
}
function tsToDate(ts) {
  if (!ts) return null;
  return ts instanceof Timestamp ? ts.toDate() : new Date(ts);
}

function emptyTimeTaken() {
  return { value: HOURS_MIN, unit: "hours", exceeded: false, note: "" };
}

// ---------------- UI bits ----------------
function Button({ className, ...props }) {
  return (
    <button
      {...props}
      className={cn(
        "bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300 text-white font-semibold",
        "px-4 py-2 rounded-lg transition",
        className
      )}
    />
  );
}
function GhostButton({ className, ...props }) {
  return (
    <button
      {...props}
      className={cn(
        "bg-white hover:bg-gray-50 disabled:bg-gray-50 text-gray-900 font-semibold",
        "px-4 py-2 rounded-lg border border-gray-200 transition",
        className
      )}
    />
  );
}
function Input({ className, ...props }) {
  return (
    <input
      {...props}
      className={cn(
        "w-full rounded-lg border border-gray-200 bg-white px-3 py-2",
        "focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-purple-300",
        className
      )}
    />
  );
}
function Select({ className, children, ...props }) {
  return (
    <select
      {...props}
      className={cn(
        "w-full rounded-lg border border-gray-200 bg-white px-3 py-2",
        "focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-purple-300",
        className
      )}
    >
      {children}
    </select>
  );
}
function Pill({ active, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "px-3 py-1 rounded-full text-sm font-semibold border transition",
        active
          ? "bg-purple-600 text-white border-purple-600"
          : "bg-white text-gray-800 border-gray-200 hover:bg-gray-50"
      )}
    >
      {label}
    </button>
  );
}

// ---------------- component ----------------
export default function PPMEngineerPortal() {
  // kiosk "identity"
  const [engineers, setEngineers] = useState([]);
  const [selectedEngineerId, setSelectedEngineerId] = useState("");
  const [pin, setPin] = useState("");
  const [authedEngineer, setAuthedEngineer] = useState(null);

  // jobs
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  // UI filters
  const [view, setView] = useState("today"); // today | overdue | upcoming | month | all
  const todayStart = useMemo(() => startOfDay(new Date()), []);
  const todayEnd = useMemo(() => endOfDay(new Date()), []);
  const thisYM = useMemo(() => ymString(new Date().getFullYear(), new Date().getMonth() + 1), []);

  // sign-off modal
  const [notesOpen, setNotesOpen] = useState(false);
  const [notesJob, setNotesJob] = useState(null);
  const [notesText, setNotesText] = useState("");

  // ✅ NEW: time taken
  const [timeTaken, setTimeTaken] = useState(emptyTimeTaken);
  const [timeTakenValid, setTimeTakenValid] = useState(true);

  // load engineers
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const snap = await getDocs(collection(db, "ppmengineers"));
        const list = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(x => x && x.active !== false)
          .sort((a,b) => String(a.name||"").localeCompare(String(b.name||"")));

        if (cancelled) return;
        setEngineers(list);
        if (!selectedEngineerId && list[0]?.id) setSelectedEngineerId(list[0].id);
      } catch (e) {
        if (!cancelled) setErr(e?.message || "Failed to load engineers.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, []); // eslint-disable-line

  const selectedEngineer = useMemo(
    () => engineers.find(e => e.id === selectedEngineerId) || null,
    [engineers, selectedEngineerId]
  );

  function handleLogin() {
    setErr("");
    setMsg("");
    if (!selectedEngineer) return setErr("Pick an engineer.");
    if (!String(pin || "").trim()) return setErr("Enter PIN.");
    if (String(pin).trim() !== String(selectedEngineer.pin || "").trim()) {
      return setErr("Wrong PIN");
    }
    setAuthedEngineer({ id: selectedEngineer.id, name: selectedEngineer.name || selectedEngineer.id });
    setPin("");
  }

  function handleLogout() {
    setAuthedEngineer(null);
    setJobs([]);
    setView("today");
    setMsg("");
    setErr("");
  }

  // subscribe to jobs for this engineer
  useEffect(() => {
    if (!authedEngineer?.id) return;

    setLoading(true);
    setErr("");

    const qy = query(
      collection(db, "ppmjobs"),
      where("assignedEngineerId", "==", authedEngineer.id),
      orderBy("dueDate", "asc")
    );

    const unsub = onSnapshot(
      qy,
      (snap) => {
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setJobs(list);
        setLoading(false);
      },
      (e) => {
        setErr(e?.message || "Failed to load jobs.");
        setLoading(false);
      }
    );

    return () => unsub();
  }, [authedEngineer?.id]);

  const filteredJobs = useMemo(() => {
    const list = Array.isArray(jobs) ? jobs : [];
    return list.filter((j) => {
      const due = tsToDate(j.dueDate);
      if (!due) return false;

      const status = j.status || "open";
      const isDone = status === "done";

      if (view === "all") return true;
      if (view === "today") return due >= todayStart && due <= todayEnd;
      if (view === "overdue") return !isDone && due < todayStart;
      if (view === "upcoming") return due > todayEnd;
      if (view === "month") return String(j.yearMonth || "") === String(thisYM);
      return true;
    });
  }, [jobs, view, todayStart, todayEnd, thisYM]);

  const counts = useMemo(() => {
    const list = Array.isArray(jobs) ? jobs : [];
    const c = { today: 0, overdue: 0, upcoming: 0, month: 0, all: list.length };
    for (const j of list) {
      const due = tsToDate(j.dueDate);
      if (!due) continue;
      const isDone = (j.status || "open") === "done";
      if (due >= todayStart && due <= todayEnd) c.today++;
      if (!isDone && due < todayStart) c.overdue++;
      if (due > todayEnd) c.upcoming++;
      if (String(j.yearMonth || "") === String(thisYM)) c.month++;
    }
    return c;
  }, [jobs, todayStart, todayEnd, thisYM]);

  function openNotes(job) {
    setNotesJob(job);
    setNotesText(job?.completionNotes || "");

    const existingMinutes = Number(job?.timeTakenMins);
    const nextTimeTaken =
      job?.timeTakenUnit && job?.timeTakenValue
        ? {
            value: Number(job.timeTakenValue),
            unit: job.timeTakenUnit,
            exceeded: Boolean(job.timeTakenExceededLimit),
            note: job.timeTakenNote || "",
          }
        : Number.isFinite(existingMinutes) && existingMinutes > 0
        ? {
            value: existingMinutes / 60,
            unit: "hours",
            exceeded: false,
            note: "",
          }
        : emptyTimeTaken();

    setTimeTaken(nextTimeTaken);
    setTimeTakenValid(validateTimeTaken(nextTimeTaken).ok);
    setNotesOpen(true);
  }
  function closeNotes() {
    setNotesOpen(false);
    setNotesJob(null);
    setNotesText("");
    setTimeTaken(emptyTimeTaken());
    setTimeTakenValid(true);
  }

  async function markDone(job, withNotes = false) {
    if (!authedEngineer) return;
    setErr("");
    setMsg("");

    const validation = validateTimeTaken(timeTaken);
    if (!validation.ok) {
      setErr(validation.error);
      return;
    }

    try {
      setBusyId(job.id);

      const { minutes, ...timeTakenFields } = buildTimeTakenCore(timeTaken);
      const payload = {
        status: "done",
        completedAt: serverTimestamp(),
        completedByEngineerId: authedEngineer.id,
        completedByEngineerName: authedEngineer.name,
        updatedAt: serverTimestamp(),
        ...timeTakenFields,
        timeTakenMins: minutes,
      };

      if (withNotes) {
        payload.completionNotes = (notesText || "").trim() || null;
      } else {
        // if you want to allow no notes, do nothing
      }


      await updateDoc(doc(db, "ppmjobs", job.id), payload);

      setMsg(`Marked done: ${job.taskName || "job"} (${isoDate(tsToDate(job.dueDate))})`);
      closeNotes();
    } catch (e) {
      setErr(e?.message || "Failed to sign off job.");
    } finally {
      setBusyId("");
    }
  }

  // ---------------- render ----------------
  if (!authedEngineer) {
    return (
      <div className="p-6 max-w-xl space-y-4">
        <h1 className="text-2xl font-extrabold text-gray-900">Engineer Portal</h1>
        <p className="text-gray-600">
          Select who is logging in, enter PIN, then you’ll see your assigned jobs.
        </p>

        {err ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-red-800">
            <div className="font-bold">Problem</div>
            <div className="mt-1">{err}</div>
          </div>
        ) : null}

        <div className="rounded-2xl border border-gray-200 bg-white p-5 space-y-3">
          <div>
            <div className="text-sm font-semibold text-gray-800">Who is logging in?</div>
            <Select value={selectedEngineerId} onChange={(e) => setSelectedEngineerId(e.target.value)}>
              <option value="">Select engineer…</option>
              {engineers.map((e) => (
                <option key={e.id} value={e.id}>{e.name || e.id}</option>
              ))}
            </Select>
          </div>

          <div>
            <div className="text-sm font-semibold text-gray-800">PIN</div>
            <Input
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              inputMode="numeric"
              placeholder="e.g. 1234"
              type="password"
            />
            <div className="text-xs text-gray-500 mt-1">Not secure — just identity selection.</div>
          </div>

          <div className="flex gap-2">
            <Button onClick={handleLogin} disabled={!selectedEngineerId || !pin.trim()}>
              Continue
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-gray-900">Jobs — {authedEngineer.name}</h1>
          <p className="text-gray-600 mt-1">
            Sign off jobs when complete. This updates the job doc in <span className="font-semibold">ppmjobs</span>.
          </p>
        </div>
        <GhostButton onClick={handleLogout}>Switch engineer</GhostButton>
      </div>

      {err ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-red-800">
          <div className="font-bold">Problem</div>
          <div className="mt-1">{err}</div>
        </div>
      ) : null}

      {msg ? (
        <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-green-800">
          <div className="font-bold">OK</div>
          <div className="mt-1">{msg}</div>
        </div>
      ) : null}

      {/* view pills */}
      <div className="rounded-2xl border border-gray-200 bg-white p-4">
        <div className="flex flex-wrap gap-2">
          <Pill active={view === "today"} onClick={() => setView("today")} label={`Today (${counts.today})`} />
          <Pill active={view === "overdue"} onClick={() => setView("overdue")} label={`Overdue (${counts.overdue})`} />
          <Pill active={view === "upcoming"} onClick={() => setView("upcoming")} label={`Upcoming (${counts.upcoming})`} />
          <Pill active={view === "month"} onClick={() => setView("month")} label={`This month (${counts.month})`} />
          <Pill active={view === "all"} onClick={() => setView("all")} label={`All (${counts.all})`} />
        </div>
      </div>

      {/* job list */}
      <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
        <div className="p-4 border-b border-gray-100">
          <div className="text-lg font-extrabold text-gray-900">Assigned jobs</div>
          <div className="text-sm text-gray-600">
            Showing: <span className="font-semibold">{view}</span>
          </div>
        </div>

        {loading ? (
          <div className="p-6 text-gray-600">Loading…</div>
        ) : filteredJobs.length === 0 ? (
          <div className="p-6 text-gray-600">No jobs in this view.</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filteredJobs.map((j) => {
              const due = tsToDate(j.dueDate);
              const isDone = (j.status || "open") === "done";
              const dueStr = due ? isoDate(due) : "—";

              return (
                <div key={j.id} className="p-4 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="font-extrabold text-gray-900 truncate">
                        {j.assetName || "Machine"} — {j.taskName || "Task"}
                      </div>
                      {isDone ? (
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full border border-green-200 bg-green-50 text-green-800">
                          Done
                        </span>
                      ) : (
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full border border-gray-200 bg-white text-gray-700">
                          Open
                        </span>
                      )}
                    </div>

                    <div className="text-sm text-gray-600 mt-1">
                      Due: <span className="font-semibold">{dueStr}</span>
                      {j.assetCode ? <span className="ml-2 text-gray-400">· {j.assetCode}</span> : null}
                    </div>

                    {j.timeTakenLabel ? (
                      <div className="text-xs text-gray-500 mt-1">
                        Time: <span className="font-semibold">{j.timeTakenLabel}</span>
                      </div>
                    ) : null}

                    {j.completionNotes ? (
                      <div className="text-xs text-gray-500 mt-1">
                        Notes: <span className="font-semibold">{j.completionNotes}</span>
                      </div>
                    ) : null}
                  </div>

                  <div className="flex gap-2 shrink-0">
                    {isDone ? (
                      <GhostButton disabled className="opacity-70">Completed</GhostButton>
                    ) : (
                      <>
                        <GhostButton
                          onClick={() => openNotes(j)}
                          disabled={busyId === j.id}
                        >
                          Done + notes
                        </GhostButton>
                        <Button
                          onClick={() => openNotes(j)} // force modal
                          disabled={busyId === j.id}
                        >
                          Complete
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* notes modal */}
      {notesOpen && notesJob ? (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4">
          <div className="w-full max-w-lg rounded-2xl bg-white border border-gray-200 shadow-xl overflow-hidden">
            <div className="p-4 border-b border-gray-100 flex items-start justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-gray-500">Complete job</div>
                <div className="text-xl font-extrabold text-gray-900">
                  {notesJob.assetName || "Machine"} — {notesJob.taskName || "Task"}
                </div>
              </div>
              <GhostButton onClick={closeNotes} disabled={busyId === notesJob.id}>Close</GhostButton>
            </div>

            <div className="p-4 space-y-3">
              <TimeTakenField
                value={timeTaken}
                onChange={(nextTimeTaken, isValid) => {
                  setTimeTaken(nextTimeTaken);
                  setTimeTakenValid(isValid);
                }}
              />

              <div>
                <div className="text-sm font-semibold text-gray-800">Completion notes (optional)</div>
                <textarea
                  value={notesText}
                  onChange={(e) => setNotesText(e.target.value)}
                  className={cn(
                    "w-full rounded-lg border border-gray-200 bg-white px-3 py-2 min-h-[120px]",
                    "focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-purple-300"
                  )}
                  placeholder="What was done? Any issues? Parts used?"
                />
              </div>
            </div>

            <div className="p-4 border-t border-gray-100 flex justify-end gap-2">
              <GhostButton onClick={closeNotes} disabled={busyId === notesJob.id}>Cancel</GhostButton>
              <Button
                onClick={() => markDone(notesJob, true)}
                disabled={busyId === notesJob.id || !timeTakenValid}
              >
                {busyId === notesJob.id ? "Saving…" : "Mark done"}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
